import {
  Box, Button, Card, CardBody, CardHeader, Heading, HStack, Icon,
  SimpleGrid, Text, VStack, Switch, FormControl, FormLabel, FormHelperText, Divider,
} from '@chakra-ui/react';
import { FiArrowLeft, FiSave, FiActivity } from 'react-icons/fi';
import { Controller } from 'react-hook-form';
import { PageContainer } from '@/client/components/layout/PageContainer';
import { LoadingSpinner } from '@/design-system/components/LoadingSpinner';
import { FormField } from '@/components/Modal/FormField';
import { useProfilingSettingsPage } from '../hooks';

export function ProfilingSettingsPage() {
  const p = useProfilingSettingsPage();

  if (!p.isSuperAdmin) {
    return (
      <PageContainer title="Access Denied">
        <Box textAlign="center" py={12}>
          <Icon as={FiActivity} boxSize={16} color="red.500" mb={4} />
          <Text color="text-primary">Super Admin Access Required</Text>
        </Box>
      </PageContainer>
    );
  }

  if (p.isLoading) return <LoadingSpinner message="Loading settings..." />;

  return (
    <PageContainer
      title="Profiling Settings"
      description="Configure profiling behavior and alert thresholds"
      action={
        <HStack spacing={3}>
          <Button leftIcon={<FiArrowLeft />} variant="outline" onClick={() => p.navigate(`/dashboard/${p.orgId}/profiling`)} borderColor="border-default" _hover={{ borderColor: 'border-hover' }}>Back</Button>
          <Button leftIcon={<FiSave />} colorScheme="brand" onClick={p.onSettingsSubmit} isLoading={p.isUpdating} isDisabled={!p.isDirty}>Save Changes</Button>
        </HStack>
      }
    >
      <VStack spacing={6} align="stretch">
        <Card variant="outline" bg="bg-surface" bgGradient={p.cardGradient}>
          <CardHeader><Heading size="sm" color="text-primary">General Settings</Heading></CardHeader>
          <CardBody pt={0}>
            <VStack spacing={4} align="stretch">
              <Controller name="enabled" control={p.control} render={({ field }) => (
                <FormControl display="flex" alignItems="center" justifyContent="space-between">
                  <Box><FormLabel mb={0} color="text-primary">Enable Profiling</FormLabel><FormHelperText mt={1} color="text-secondary">Allow on-demand profile collection via the dashboard</FormHelperText></Box>
                  <Switch colorScheme="brand" size="lg" isChecked={field.value} onChange={field.onChange} />
                </FormControl>
              )} />
              <Divider />
              <Controller name="auto_analyze" control={p.control} render={({ field }) => (
                <FormControl display="flex" alignItems="center" justifyContent="space-between">
                  <Box><FormLabel mb={0} color="text-primary">Auto-Analyze Profiles</FormLabel><FormHelperText mt={1} color="text-secondary">Automatically analyze profiles after collection</FormHelperText></Box>
                  <Switch colorScheme="brand" size="lg" isChecked={field.value} onChange={field.onChange} />
                </FormControl>
              )} />
            </VStack>
          </CardBody>
        </Card>

        <Card variant="outline" bg="bg-surface" bgGradient={p.cardGradient}>
          <CardHeader><Heading size="sm" color="text-primary">Scheduled Collection</Heading></CardHeader>
          <CardBody pt={0}>
            <VStack spacing={4} align="stretch">
              <Controller name="scheduled_enabled" control={p.control} render={({ field }) => (
                <FormControl display="flex" alignItems="center" justifyContent="space-between">
                  <Box><FormLabel mb={0} color="text-primary">Enable Scheduled Collection</FormLabel><FormHelperText mt={1} color="text-secondary">Automatically collect profiles on a schedule</FormHelperText></Box>
                  <Switch colorScheme="brand" size="lg" isChecked={field.value} onChange={field.onChange} />
                </FormControl>
              )} />
              <FormField label="Schedule (Cron Expression)" name="schedule_cron" register={p.register} error={p.errors.schedule_cron} placeholder="0 */4 * * *" description="Standard cron expression (e.g., '0 */4 * * *' for every 4 hours)" />
            </VStack>
          </CardBody>
        </Card>

        <Card variant="outline" bg="bg-surface" bgGradient={p.cardGradient}>
          <CardHeader><Heading size="sm" color="text-primary">Collection Settings</Heading></CardHeader>
          <CardBody pt={0}>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <FormField label="CPU Sample Rate (Hz)" name="cpu_sample_rate_hz" register={p.register} error={p.errors.cpu_sample_rate_hz} type="number" min={10} max={500} description="Samples per second for CPU profiling (10-500)" />
              <FormField label="Retention Days" name="retention_days" register={p.register} error={p.errors.retention_days} type="number" min={1} max={365} description="How long to keep profiles before deletion (1-365)" />
            </SimpleGrid>
          </CardBody>
        </Card>

        <Card variant="outline" bg="bg-surface" bgGradient={p.cardGradient}>
          <CardHeader><Heading size="sm" color="text-primary">Alert Thresholds</Heading></CardHeader>
          <CardBody pt={0}>
            <Text color="text-secondary" fontSize="sm" mb={4}>Configure thresholds that trigger alerts during profile analysis</Text>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
              <FormField label="Goroutine Count" name="alert_goroutine_count" register={p.register} error={p.errors.alert_goroutine_count} type="number" min={100} max={100000} description="Alert when goroutines exceed this count" />
              <FormField label="Heap Allocation (MB)" name="alert_heap_alloc_mb" register={p.register} error={p.errors.alert_heap_alloc_mb} type="number" min={64} max={32768} description="Alert when heap exceeds this size in MB" />
              <FormField label="CPU Usage (%)" name="alert_cpu_percent" register={p.register} error={p.errors.alert_cpu_percent} type="number" min={10} max={100} description="Alert when CPU usage exceeds this percentage" />
            </SimpleGrid>
          </CardBody>
        </Card>
      </VStack>
    </PageContainer>
  );
}
